const DEFAULT_MAX_BYTES = 10 * 1024 * 1024
const DEFAULT_DROP_ACTIVE_CLASSES = ["ring-2", "ring-emerald-400", "ring-inset"]

export const chatAttachmentSupport = (Base) => class extends Base {
  get attachmentMaxBytes() {
    return DEFAULT_MAX_BYTES
  }

  get attachmentPreviewSizeClass() {
    return "h-16 w-16"
  }

  get attachmentDropActiveClasses() {
    return DEFAULT_DROP_ACTIVE_CLASSES
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    this.processAttachmentFile(file)
  }

  handlePaste(event) {
    const file = this.extractImageFromClipboard(event)
    if (!file) return

    event.preventDefault()
    this.processAttachmentFile(file)
  }

  handleDrop(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    event.stopPropagation()
    this.setDropActive(false)

    const file = this.extractImageFromDataTransfer(event.dataTransfer)
    if (!file) return

    this.processAttachmentFile(file)
  }

  handleDragOver(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    this.setDropActive(true)
  }

  handleDragEnter(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    this.setDropActive(true)
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.setDropActive(false)
  }

  handleDragEnd() {
    this.setDropActive(false)
  }

  processAttachmentFile(file) {
    if (!this.validateAttachmentFile(file)) return
    this.uploadFile(file)
  }

  validateAttachmentFile(file) {
    if (!this.isImageFile(file)) {
      alert("Only image files are allowed")
      this.resetFileInput()
      return false
    }

    if (file.size > this.attachmentMaxBytes) {
      const maxSizeMb = Math.round(this.attachmentMaxBytes / (1024 * 1024))
      alert(`File size must be less than ${maxSizeMb}MB`)
      this.resetFileInput()
      return false
    }

    return true
  }

  isImageFile(file) {
    return file && file.type && file.type.startsWith("image/")
  }

  resetFileInput() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }
  }

  extractImageFromClipboard(event) {
    const items = Array.from(event.clipboardData?.items || [])
    for (const item of items) {
      if (item.kind !== "file") continue
      const file = item.getAsFile()
      if (this.isImageFile(file)) return file
    }

    const files = Array.from(event.clipboardData?.files || [])
    return files.find((file) => this.isImageFile(file)) || null
  }

  extractImageFromDataTransfer(dataTransfer) {
    const files = Array.from(dataTransfer?.files || [])
    return files.find((file) => this.isImageFile(file)) || null
  }

  eventHasFiles(event) {
    const types = event?.dataTransfer?.types
    return types && Array.from(types).includes("Files")
  }

  setDropActive(isActive) {
    if (!this.hasDropZoneTarget) return

    const classes = this.attachmentDropActiveClasses
    this.dropZoneTargets.forEach((zone) => {
      classes.forEach((className) => {
        zone.classList.toggle(className, isActive)
      })
    })
  }

  renderAttachmentPreview(dataUrl) {
    if (!this.hasAttachmentPreviewTarget) return

    const action = `click->${this.identifier}#clearAttachment`
    const sizeClass = this.attachmentPreviewSizeClass

    this.attachmentPreviewTarget.innerHTML = `
      <div class="relative inline-block">
        <img src="${dataUrl}" class="${sizeClass} object-cover rounded" />
        <button type="button" data-action="${action}" class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">&times;</button>
      </div>
    `
    this.attachmentPreviewTarget.classList.remove("hidden")
  }

  async uploadFile(file) {
    const reader = new FileReader()
    reader.onload = (event) => {
      if (event?.target?.result) {
        this.renderAttachmentPreview(event.target.result)
      }
    }
    reader.readAsDataURL(file)

    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.remove("hidden")
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }

    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch("/upload/chat_attachments", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error("Upload failed")
      }

      const data = await response.json()
      if (this.hasAttachmentDataTarget) {
        this.attachmentDataTarget.value = JSON.stringify(data)
      }
    } catch (error) {
      console.error("Upload error:", error)
      alert("Failed to upload file. Please try again.")
      this.clearAttachment()
    } finally {
      if (this.hasUploadProgressTarget) {
        this.uploadProgressTarget.classList.add("hidden")
      }
      if (this.hasSendButtonTarget) {
        this.sendButtonTarget.disabled = false
      }
    }
  }

  clearAttachment() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }
    if (this.hasAttachmentPreviewTarget) {
      this.attachmentPreviewTarget.innerHTML = ""
      this.attachmentPreviewTarget.classList.add("hidden")
    }
    if (this.hasAttachmentDataTarget) {
      this.attachmentDataTarget.value = ""
    }
  }
}
